import useI18n from '@/hooks/useI18n';
import CustomProgressBar from '../CustomProgressBar';
import { Campaign } from '@/interfaces/campaign';
import { formatCampaignDate } from '@/utils/dateUtils';
import { formatMoney } from '@/utils/numberUtils';
import { useContext } from 'react';
import { UserContext } from '@/context/UserContext';
interface CampaignDetailComponentProps {
  campaign: Campaign;
}

const CampaignDetailComponent: React.FC<CampaignDetailComponentProps> = ({
  campaign,
}) => {
  const { t } = useI18n();
  const startDate = formatCampaignDate(campaign.startDate, false);
  const endDate = formatCampaignDate(campaign.endDate ?? '', false);
  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { isDummyUser } = userContext;
  const data = [
    { label: t('common.startDate'), value: startDate },
    {
      label: t('common.endDate'),
      value:
        campaign.endDate && !campaign.runCampaignTillMoneyEnd
          ? endDate
          : t('common.untilBudgetEnds'),
    },
    {
      label: t('common.budget'),
      value: isDummyUser
        ? formatMoney(10000)
        : formatMoney(campaign.campaignBudget),
    },
    {
      label: t('common.remainingBalance'),
      // Derived: budget (10,000) − cashback paid (1,840) = 8,160.
      value: isDummyUser
        ? formatMoney(10000 - 1840)
        : formatMoney(campaign.remainingCampaignBudget),
    },
  ];

  return (
    <div className="flex flex-col" style={{ gap: 32 }}>
      <div
        className="bg-white"
        style={{
          width: 128,
          height: 128,
          borderRadius: 121,
          border: '8px solid #FFFFFF',
          boxShadow: '0px 2px 8px 0px #00000014',
          overflow: 'hidden',
        }}
      >
        <img
          src={
            campaign?.brandImageUrl
              ? campaign?.brandImageUrl
              : '../assets/uploadBlueIcon.svg'
          }
          alt={String(t('campaign.altTexts.brandLogo'))}
          className="w-full h-full object-cover"
          style={{ borderRadius: 104 }}
        />
      </div>

      <div className="flex flex-col" style={{ gap: 28 }}>
        <div className="flex items-center" style={{ gap: 8 }}>
          <span
            className="break-words whitespace-normal capitalize"
            style={{
              color: '#121111',
              fontFamily: 'Manrope, sans-serif',
              fontSize: 20,
              lineHeight: '24px',
              fontWeight: 500,
            }}
          >
            {campaign.brandName}
          </span>
          <span
            className="inline-flex items-center"
            style={{
              padding: '4px 8px',
              borderRadius: 49,
              backgroundColor: '#FFA5001A',
              color: '#FFA500',
              fontFamily: 'Manrope, sans-serif',
              fontSize: 14,
              lineHeight: '16px',
              fontWeight: 500,
            }}
          >
            {t(`common.${campaign.status}`)}
          </span>
        </div>

        <div className="flex" style={{ gap: 32 }}>
          {data.map((item, index) => (
            <div key={index} className="flex flex-col" style={{ gap: 8 }}>
              <div
                style={{
                  color: '#9B919D',
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: 14,
                  lineHeight: '16px',
                  fontWeight: 400,
                }}
              >
                {item.label}
              </div>
              <div className="flex items-center" style={{ gap: 14 }}>
                <div
                  style={{
                    color: '#292828',
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: 18,
                    lineHeight: '18px',
                    fontWeight: 500,
                  }}
                >
                  {item.value}
                </div>
                {item.label === t('common.remainingBalance') && (
                  <div style={{ width: 133 }}>
                    <CustomProgressBar
                      percentage={
                        isDummyUser
                          ? ((10000 - 1840) / 10000) * 100
                          : campaign.campaignBudget
                            ? (campaign.remainingCampaignBudget /
                                campaign.campaignBudget) *
                              100
                            : 0
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div
          className="w-full"
          style={{
            color: '#292828',
            fontFamily: 'Manrope, sans-serif',
            fontSize: 18,
            lineHeight: '120%',
            fontWeight: 500,
          }}
        >
          {campaign.brandDescription}
        </div>
      </div>

      <div
        className="w-full"
        style={{ height: 0, borderTop: '1px solid #E5E9EB' }}
      />
    </div>
  );
};

export default CampaignDetailComponent;
